import { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Alert, Button, Card, Form } from 'react-bootstrap';
import type { RecommendationLabPreviewResult, SkinMetricKey } from '@/models/RecommendationLab';
import type { RecommendationEnvironmentFormValue } from '@/pages/recommendation-lab/environment';
import type { SavedValidationCase } from '@/pages/recommendation-lab/validation';
import { hasLegacySavedCases, loadSavedCases, persistSavedCases } from '@/pages/recommendation-lab/caseStorage';
import { PRESETS, type LabPreset } from '@/pages/recommendation-lab/data';
import {
  collectRankingPolicyIssues,
  type RecommendationLabRankingPercentages,
} from '@/pages/recommendation-lab/rankingPolicy';

interface RecommendationLabCaseControlsProps {
  readonly surveyVersion: string | null;
  readonly metricScores: Record<SkinMetricKey, number>;
  readonly surveyAnswers: Record<string, string[]>;
  readonly environment: RecommendationEnvironmentFormValue;
  readonly rankingPercentages: RecommendationLabRankingPercentages;
  readonly baselineResult: RecommendationLabPreviewResult | null;
  readonly onApplyPreset: (preset: LabPreset) => void;
  readonly onRestoreCase: (saved: SavedValidationCase) => void;
}

export function RecommendationLabCaseControls({
  surveyVersion,
  metricScores,
  surveyAnswers,
  environment,
  rankingPercentages,
  baselineResult,
  onApplyPreset,
  onRestoreCase,
}: RecommendationLabCaseControlsProps) {
  const [savedCases, setSavedCases] = useState<SavedValidationCase[]>([]);
  const [caseName, setCaseName] = useState('');
  const [legacyCasesDiscarded, setLegacyCasesDiscarded] = useState(false);
  const rankingPolicyValid = collectRankingPolicyIssues(rankingPercentages).length === 0;

  useEffect(() => {
    setSavedCases(loadSavedCases());
    setLegacyCasesDiscarded(hasLegacySavedCases());
  }, []);

  const replaceSavedCases = (cases: SavedValidationCase[]) => {
    setSavedCases(cases);
    persistSavedCases(cases);
  };

  const saveCurrentCase = () => {
    if (!surveyVersion || !rankingPolicyValid) {
      return;
    }
    const name = caseName.trim() || `case-${new Date().toISOString().slice(0, 16)}`;
    replaceSavedCases([
      ...savedCases.filter((saved) => saved.name !== name),
      {
        schemaVersion: 'client-parity-v1',
        surveyVersion,
        name,
        savedAt: new Date().toISOString(),
        metricScores,
        surveyAnswers,
        environment,
        rankingPercentages,
        ...(baselineResult ? { baselineResult } : {}),
      },
    ]);
    setCaseName('');
  };

  return (
    <Card className="mb-3">
      <Card.Header className="py-2">Presets · Cases</Card.Header>
      <Card.Body className="py-2">
        {legacyCasesDiscarded && (
          <Alert variant="warning" className="small py-2">
            Saved legacy cases contain Admin-only overrides and cannot be replayed. Save them again with the current
            customer input contract.
          </Alert>
        )}
        <div className="d-flex flex-wrap gap-1 mb-2">
          {PRESETS.map((preset) => (
            <Button key={preset.name} size="sm" variant="outline-primary" onClick={() => onApplyPreset(preset)}>
              {preset.name}
            </Button>
          ))}
        </div>
        <div className="d-flex gap-1">
          <Form.Control
            size="sm"
            aria-label="Case name"
            placeholder="Case name"
            value={caseName}
            onChange={(event) => setCaseName(event.target.value)}
          />
          <Button
            size="sm"
            variant="outline-secondary"
            className="text-nowrap flex-shrink-0"
            onClick={saveCurrentCase}
            disabled={!surveyVersion || !rankingPolicyValid}
          >
            Save
          </Button>
        </div>
        {savedCases.length > 0 && (
          <div className="d-flex flex-wrap gap-1 mt-2">
            {savedCases.map((saved) => (
              <div
                key={saved.name}
                className="btn-group btn-group-sm"
                role="group"
                aria-label={`saved case ${saved.name}`}
              >
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => onRestoreCase(saved)}
                  disabled={surveyVersion === null}
                >
                  {saved.name}
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  title="Delete case"
                  aria-label={`Delete ${saved.name} case`}
                  onClick={() => replaceSavedCases(savedCases.filter((candidate) => candidate.name !== saved.name))}
                >
                  <Trash2 size={14} aria-hidden="true" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
